import { defineStore } from 'pinia'
import type BattleSimulatorShipDesign from '@/types/BattleSimulator/BattleSimulatorShipDesign'
import type PlayerShipDesign from '@/types/PlayerShipDesign'
import type { BattleSimulatorResult, BattleShip, BattleEvent } from '@/types/BattleSimulator/BattleSimulatorResult'
import { useShipDesignStore } from '@/stores/shipDesignStore'
import { useShipStore } from '@/stores/shipStore'

/**
 * Calculate damage based on the shield to attack ratio
 * Exact replica of backend Battle::calculateDamage()
 */
function calculateDamage(attack: number, shields: number): number {
  if (attack === 0) {
    return 0
  }

  // Calculate a continuous damage reduction based on the shield-to-attack ratio
  const ratio = shields / attack
  const damageMultiplier = 1 / (1 + Math.log(ratio + 1))

  // Calculate base damage
  let damage = Math.ceil(attack * damageMultiplier)

  // Apply critical hit (5% chance for 150% damage)
  if (Math.random() * 100 < 5) {
    damage = Math.floor(damage * 1.5)
  }

  return Math.max(1, damage)
}

/**
 * Get a random ship that is not destroyed from an array
 */
function getRandomShipNotDestroyed(ships: BattleShip[]): BattleShip | null {
  const aliveShips = ships.filter(ship => !ship.destroyed)
  if (aliveShips.length === 0) {
    return null
  }
  return aliveShips[Math.floor(Math.random() * aliveShips.length)]
}

export const useBattleSimulatorStore = defineStore('battleSimulatorStore', {
  state: () => ({
    attackingFleet: new Map<number, BattleSimulatorShipDesign>(),
    defendingFleet: new Map<number, BattleSimulatorShipDesign>(),
    planetPopulation: 30,
    troopPercentage: 100,
    planetEnergy: 0,
    shieldPercentage: 100,
    planetVships: 0,
    planetShields: 0,
    planetFocusFire: false,
    lastBattleResult: null as BattleSimulatorResult | null
  }),
  getters: {
    attackingTotals: (state) => {
      let hitpoints = 0
      let attackVsShips = 0
      let attackVsPlanet = 0
      let shields = 0
      let quantity = 0
      state.attackingFleet.forEach((design) => {
        hitpoints += design.hitpoints * design.quantity
        attackVsShips += design.attackVsShips * design.quantity
        attackVsPlanet += design.attackVsPlanet * design.quantity
        shields += design.shields * design.quantity
        quantity += design.quantity
      })
      return { hitpoints, attackVsShips, attackVsPlanet, shields, quantity }
    },
    defendingTotals: (state) => {
      let hitpoints = 0
      let attackVsShips = 0
      let shields = 0
      let quantity = 0
      state.defendingFleet.forEach((design) => {
        hitpoints += design.hitpoints * design.quantity
        attackVsShips += design.attackVsShips * design.quantity
        shields += design.shields * design.quantity
        quantity += design.quantity
      })
      return { hitpoints, attackVsShips, shields, quantity }
    },
    planetTroops: (state) => {
      return Math.floor(state.planetPopulation * 100 * (state.troopPercentage / 100))
    },
    currentShieldEnergy: (state) => {
      return Math.floor(state.planetEnergy * (state.shieldPercentage / 100))
    }
  },
  actions: {
    createFromPlayerShipDesign(design: PlayerShipDesign, quantity: number = 1): BattleSimulatorShipDesign {
      return {
        id: design.id,
        name: design.name,
        hitpoints: design.hitPoints,
        attackVsShips: design.attackShips,
        attackVsPlanet: design.attackPlanets,
        shields: design.shields,
        quantity
      }
    },
    addAttackingFromPlayerDesign(designId: number, quantity: number = 1) {
      const shipDesignStore = useShipDesignStore()
      const design = shipDesignStore.getShipDesignById(designId)
      if (design) {
        this.attackingFleet.set(design.id, this.createFromPlayerShipDesign(design, quantity))
      }
    },
    addDefendingFromPlayerDesign(designId: number, quantity: number = 1) {
      const shipDesignStore = useShipDesignStore()
      const design = shipDesignStore.getShipDesignById(designId)
      if (design) {
        const simDesign = this.createFromPlayerShipDesign(design, quantity)
        simDesign.attackVsPlanet = 0 // Defending fleet doesn't attack planets
        this.defendingFleet.set(design.id, simDesign)
      }
    },
    addAttackingShipDesign(design: BattleSimulatorShipDesign) {
      this.attackingFleet.set(design.id, design)
    },
    removeAttackingShipDesign(id: number) {
      this.attackingFleet.delete(id)
    },
    updateAttackingShipDesign(id: number, design: Partial<BattleSimulatorShipDesign>) {
      const existing = this.attackingFleet.get(id)
      if (existing) {
        this.attackingFleet.set(id, { ...existing, ...design })
      }
    },
    addDefendingShipDesign(design: BattleSimulatorShipDesign) {
      this.defendingFleet.set(design.id, design)
    },
    removeDefendingShipDesign(id: number) {
      this.defendingFleet.delete(id)
    },
    updateDefendingShipDesign(id: number, design: Partial<BattleSimulatorShipDesign>) {
      const existing = this.defendingFleet.get(id)
      if (existing) {
        this.defendingFleet.set(id, { ...existing, ...design })
      }
    },
    increaseAttackingQuantity(id: number, amount: number = 1) {
      const design = this.attackingFleet.get(id)
      if (design) {
        design.quantity += amount
      }
    },
    decreaseAttackingQuantity(id: number, amount: number = 1) {
      const design = this.attackingFleet.get(id)
      if (design && design.quantity > amount) {
        design.quantity -= amount
      } else {
        this.attackingFleet.delete(id)
      }
    },
    increaseDefendingQuantity(id: number, amount: number = 1) {
      const design = this.defendingFleet.get(id)
      if (design) {
        design.quantity += amount
      }
    },
    decreaseDefendingQuantity(id: number, amount: number = 1) {
      const design = this.defendingFleet.get(id)
      if (design && design.quantity > amount) {
        design.quantity -= amount
      } else {
        this.defendingFleet.delete(id)
      }
    },
    clearAttackingFleet() {
      this.attackingFleet.clear()
    },
    clearDefendingFleet() {
      this.defendingFleet.clear()
    },
    resetPlanetDefense() {
      this.planetPopulation = 30
      this.troopPercentage = 100
      this.planetEnergy = 0
      this.shieldPercentage = 100
      this.planetVships = 0
      this.planetShields = 0
      this.planetFocusFire = false
    },
    copyFromFleet(fleetId: number, target: 'attacking' | 'defending') {
      const shipStore = useShipStore()
      const shipDesignStore = useShipDesignStore()
      // Only include ships in the core fleet (shipGroupId === 0), not ships in squads
      const ships = shipStore.getShipsFromFleet(fleetId).filter(ship => ship.shipGroupId === 0)

      // Group ships by design and count quantities
      const designCounts = new Map<number, { ship: typeof ships[0], count: number }>()
      for (const ship of ships) {
        const existing = designCounts.get(ship.shipDesignId)
        if (existing) {
          existing.count++
        } else {
          designCounts.set(ship.shipDesignId, { ship, count: 1 })
        }
      }

      // Clear the target fleet
      if (target === 'attacking') {
        this.attackingFleet.clear()
      } else {
        this.defendingFleet.clear()
      }

      // Add each design with its quantity
      designCounts.forEach(({ ship, count }, designId) => {
        const design = shipDesignStore.getShipDesignById(designId)
        const simDesign: BattleSimulatorShipDesign = {
          id: designId,
          name: design?.name ?? ship.shipName,
          hitpoints: ship.maxHitPoints,
          attackVsShips: ship.attackShips,
          attackVsPlanet: target === 'attacking' ? ship.attackPlanets : 0,
          shields: ship.shields,
          quantity: count
        }

        if (target === 'attacking') {
          this.attackingFleet.set(designId, simDesign)
        } else {
          this.defendingFleet.set(designId, simDesign)
        }
      })
    },

    /**
     * Run the battle simulation
     * Exact replica of backend Battle logic
     */
    runBattle(): BattleSimulatorResult {
      const events: BattleEvent[] = []
      let rounds = 0

      // Damage tracking
      let attackerDamageTakenFleetVsFleet = 0
      let attackerDamageTakenPlanetVsFleet = 0
      let defenderDamageTakenFleetVsFleet = 0
      let defenderDamageTakenFleetVsPlanet = 0

      // Create individual ship objects for attackers
      const attackingShips: BattleShip[] = []
      let shipIdCounter = 1
      this.attackingFleet.forEach((design) => {
        for (let i = 0; i < design.quantity; i++) {
          attackingShips.push({
            id: shipIdCounter++,
            name: design.name,
            hitpoints: design.hitpoints,
            maxHitpoints: design.hitpoints,
            attackVsShips: design.attackVsShips,
            attackVsPlanet: design.attackVsPlanet,
            shields: design.shields,
            destroyed: false,
            isAttacker: true
          })
        }
      })

      // Create individual ship objects for defenders
      const defendingShips: BattleShip[] = []
      this.defendingFleet.forEach((design) => {
        for (let i = 0; i < design.quantity; i++) {
          defendingShips.push({
            id: shipIdCounter++,
            name: design.name,
            hitpoints: design.hitpoints,
            maxHitpoints: design.hitpoints,
            attackVsShips: design.attackVsShips,
            attackVsPlanet: 0, // Defenders don't attack planets
            shields: design.shields,
            destroyed: false,
            isAttacker: false
          })
        }
      })

      // Planet defense stats
      // Full max values (without percentage reduction)
      const planetHitPointsMax = this.planetPopulation * 100
      const planetShieldEnergyMax = this.planetEnergy
      // Current values (with percentage reduction applied)
      let planetHitPoints = this.planetTroops
      let planetShieldEnergy = this.currentShieldEnergy
      const planetAttack = Math.max(this.planetVships, 0)
      const planetShieldsValue = this.planetShields
      // Use current values (with percentage) to determine if planet has defense
      const hasPlanetDefense = planetHitPoints > 0 || planetShieldEnergy > 0 || planetAttack > 0

      // Track starting stats
      const attackerShipsStart = attackingShips.length
      const defenderShipsStart = defendingShips.length
      const planetShieldsStart = planetShieldEnergyMax > 0
        ? Math.round((planetShieldEnergy / planetShieldEnergyMax) * 100)
        : 0
      const planetTroopsStart = planetHitPointsMax > 0
        ? Math.round((planetHitPoints / planetHitPointsMax) * 100)
        : 0

      // Focus fire targets
      let attackerFocusTarget: BattleShip | null = null
      let defenderFocusTarget: BattleShip | null = null

      // Phase 1: Ship vs Ship combat
      if (attackingShips.length > 0 && defendingShips.length > 0) {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          rounds++
          let totalDamage = 0

          const maxAttacks = Math.max(attackingShips.length, defendingShips.length)

          for (let i = 0; i < maxAttacks; i++) {
            // Attacker attacks
            if (i < attackingShips.length) {
              const attacker = attackingShips[i]
              if (!attacker.destroyed) {
                // Focus fire logic: 2% chance to switch target
                if (attackerFocusTarget === null || attackerFocusTarget.destroyed || Math.random() * 100 < 2) {
                  attackerFocusTarget = getRandomShipNotDestroyed(defendingShips)
                }

                if (attackerFocusTarget) {
                  const damage = calculateDamage(attacker.attackVsShips, attackerFocusTarget.shields)
                  attackerFocusTarget.hitpoints -= damage
                  totalDamage += damage
                  defenderDamageTakenFleetVsFleet += damage

                  events.push({
                    type: 'attack',
                    attacker: { id: `ship:${attacker.id}` },
                    defender: {
                      id: `ship:${attackerFocusTarget.id}`,
                      health: Math.round((attackerFocusTarget.hitpoints / attackerFocusTarget.maxHitpoints) * 100),
                      damage
                    }
                  })

                  if (attackerFocusTarget.hitpoints <= 0 && !attackerFocusTarget.destroyed) {
                    attackerFocusTarget.destroyed = true
                    events.push({ type: 'destroyed', id: `ship:${attackerFocusTarget.id}` })
                  }
                }
              }
            }

            // Defender attacks
            if (i < defendingShips.length) {
              const defender = defendingShips[i]
              if (!defender.destroyed) {
                // Focus fire logic: 2% chance to switch target
                if (defenderFocusTarget === null || defenderFocusTarget.destroyed || Math.random() * 100 < 2) {
                  defenderFocusTarget = getRandomShipNotDestroyed(attackingShips)
                }

                if (defenderFocusTarget) {
                  const damage = calculateDamage(defender.attackVsShips, defenderFocusTarget.shields)
                  defenderFocusTarget.hitpoints -= damage
                  totalDamage += damage
                  attackerDamageTakenFleetVsFleet += damage

                  events.push({
                    type: 'attack',
                    attacker: { id: `ship:${defender.id}` },
                    defender: {
                      id: `ship:${defenderFocusTarget.id}`,
                      health: Math.round((defenderFocusTarget.hitpoints / defenderFocusTarget.maxHitpoints) * 100),
                      damage
                    }
                  })

                  if (defenderFocusTarget.hitpoints <= 0 && !defenderFocusTarget.destroyed) {
                    defenderFocusTarget.destroyed = true
                    events.push({ type: 'destroyed', id: `ship:${defenderFocusTarget.id}` })
                  }
                }
              }
            }
          }

          // Check end conditions
          const attackersLeft = attackingShips.filter(s => !s.destroyed).length
          const defendersLeft = defendingShips.filter(s => !s.destroyed).length

          if (attackersLeft === 0 || defendersLeft === 0 || totalDamage === 0) {
            break
          }
        }
      }

      // Phase 2: Ship vs Planet combat (only if attackers remain and planet has defense)
      const aliveAttackers = attackingShips.filter(s => !s.destroyed)
      let planetFocusTarget: BattleShip | null = null

      if (aliveAttackers.length > 0 && hasPlanetDefense) {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          rounds++
          let totalDamage = 0

          // All ships attack planet first
          for (const attacker of attackingShips) {
            if (attacker.destroyed) continue
            if (planetHitPoints <= 0) break

            // Ship attacks planet
            if (attacker.attackVsPlanet > 0) {
              const damage = calculateDamage(attacker.attackVsPlanet, planetShieldsValue)
              totalDamage += damage
              defenderDamageTakenFleetVsPlanet += damage

              if (planetShieldEnergy > 0) {
                // Damage goes to shields first
                planetShieldEnergy -= damage
                if (planetShieldEnergy < 0) {
                  planetShieldEnergy = 0
                }
              } else {
                // Damage goes to planet hitpoints (troops)
                planetHitPoints -= damage

                // 4% chance to destroy building (not simulated - no buildings in simulator)
                // 4% chance to kill population (not simulated)
              }

              events.push({
                type: 'attack',
                attacker: { id: `ship:${attacker.id}` },
                defender: {
                  id: 'planet:1',
                  health: planetHitPointsMax > 0 ? Math.round((planetHitPoints / planetHitPointsMax) * 100) : 0,
                  shields: planetShieldEnergyMax > 0 ? Math.round((planetShieldEnergy / planetShieldEnergyMax) * 100) : 0,
                  damage
                }
              })
            }
          }

          // Then planet counter-attacks once per round
          if (planetAttack > 0 && planetHitPoints > 0) {
            // Focus fire logic with minister: 3% switch chance, without: random every time
            if (this.planetFocusFire) {
              if (planetFocusTarget === null || planetFocusTarget.destroyed || Math.random() * 100 < 3) {
                planetFocusTarget = getRandomShipNotDestroyed(attackingShips)
              }
            } else {
              planetFocusTarget = getRandomShipNotDestroyed(attackingShips)
            }

            if (planetFocusTarget) {
              const damage = calculateDamage(planetAttack, planetFocusTarget.shields)
              planetFocusTarget.hitpoints -= damage
              totalDamage += damage
              attackerDamageTakenPlanetVsFleet += damage

              events.push({
                type: 'attack',
                attacker: { id: 'planet:1' },
                defender: {
                  id: `ship:${planetFocusTarget.id}`,
                  health: Math.round((planetFocusTarget.hitpoints / planetFocusTarget.maxHitpoints) * 100),
                  damage
                }
              })

              if (planetFocusTarget.hitpoints <= 0 && !planetFocusTarget.destroyed) {
                planetFocusTarget.destroyed = true
                events.push({ type: 'destroyed', id: `ship:${planetFocusTarget.id}` })
              }
            }
          }

          // Check end conditions
          const attackersRemaining = attackingShips.filter(s => !s.destroyed).length

          if (attackersRemaining === 0 || planetHitPoints <= 0 || totalDamage === 0) {
            break
          }
        }
      }

      // Calculate final stats
      const attackerShipsEnd = attackingShips.filter(s => !s.destroyed).length
      const defenderShipsEnd = defendingShips.filter(s => !s.destroyed).length
      const planetShieldsEnd = planetShieldEnergyMax > 0
        ? Math.max(0, Math.round((planetShieldEnergy / planetShieldEnergyMax) * 100))
        : 0
      const planetTroopsEnd = planetHitPointsMax > 0
        ? Math.max(0, Math.round((planetHitPoints / planetHitPointsMax) * 100))
        : 0

      // Determine winner
      let winner: 'attacker' | 'defender' | 'draw'
      if (attackerShipsEnd === 0) {
        winner = 'defender'
      } else if (hasPlanetDefense) {
        winner = planetHitPoints <= 0 ? 'attacker' : 'defender'
      } else if (defenderShipsEnd === 0) {
        winner = 'attacker'
      } else {
        winner = 'draw'
      }

      events.push({ type: 'winner', winnerId: winner })

      const result: BattleSimulatorResult = {
        winner,
        attackerShipsStart,
        attackerShipsEnd,
        defenderShipsStart,
        defenderShipsEnd,
        planetShieldsStart,
        planetShieldsEnd,
        planetTroopsStart,
        planetTroopsEnd,
        events,
        rounds,
        attackerDamageTakenFleetVsFleet,
        attackerDamageTakenPlanetVsFleet,
        defenderDamageTakenFleetVsFleet,
        defenderDamageTakenFleetVsPlanet
      }

      this.lastBattleResult = result
      return result
    }
  },
  persist: {
    key: 'battleSimulatorStore',
    storage: localStorage,
    serializer: {
      serialize: (state) => {
        return JSON.stringify({
          attackingFleet: Array.from(state.attackingFleet.entries()),
          defendingFleet: Array.from(state.defendingFleet.entries()),
          planetPopulation: state.planetPopulation,
          troopPercentage: state.troopPercentage,
          planetEnergy: state.planetEnergy,
          shieldPercentage: state.shieldPercentage,
          planetVships: state.planetVships,
          planetShields: state.planetShields,
          planetFocusFire: state.planetFocusFire
          // Don't persist lastBattleResult
        })
      },
      deserialize: (value) => {
        const parsed = JSON.parse(value)
        return {
          attackingFleet: new Map(parsed.attackingFleet || []),
          defendingFleet: new Map(parsed.defendingFleet || []),
          planetPopulation: parsed.planetPopulation ?? 30,
          troopPercentage: parsed.troopPercentage ?? 100,
          planetEnergy: parsed.planetEnergy ?? 0,
          shieldPercentage: parsed.shieldPercentage ?? 100,
          planetVships: parsed.planetVships ?? 0,
          planetShields: parsed.planetShields ?? 0,
          planetFocusFire: parsed.planetFocusFire ?? false,
          lastBattleResult: null
        }
      }
    }
  }
})
